import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import {
  Clock,
  TrendingUp,
  TrendingDown,
  Calendar,
  Activity,
  BarChart3,
  Users,
  Zap,
  Sun,
  Moon,
  Sunrise,
  Loader2,
  Globe
} from 'lucide-react';

// Hour chart component
const HourlyChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  // Group hours into work hours highlight
  const isWorkHour = (hour) => hour >= 9 && hour <= 18;
  
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between h-40 gap-0.5">
        {data.map((item, index) => (
          <div key={index} className="flex flex-col items-center flex-1 group relative">
            <motion.div
              className={`w-full rounded-t-sm min-h-[2px] ${
                isWorkHour(item.hour)
                  ? 'bg-gradient-to-t from-green-600 to-green-400'
                  : 'bg-gradient-to-t from-blue-600 to-blue-400'
              }`}
              initial={{ height: 0 }}
              animate={{ height: `${(item.count / maxCount) * 100}%` }}
              transition={{ duration: 0.5, delay: index * 0.02 }}
            />
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-slate-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                {item.hour}:00 - {item.count} actividades
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-500">
        <span>0h</span>
        <span>6h</span>
        <span className="text-green-400">12h</span>
        <span>18h</span>
        <span>23h</span>
      </div>
      <div className="flex items-center justify-center gap-4 text-xs mt-2">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          Horario laboral
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500"></div>
          Fuera de horario
        </span>
      </div>
    </div>
  );
};

// Day of week chart component
const DailyChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const colors = [
    'from-blue-500 to-blue-400',
    'from-cyan-500 to-cyan-400',
    'from-teal-500 to-teal-400',
    'from-green-500 to-green-400',
    'from-emerald-500 to-emerald-400',
    'from-amber-500 to-amber-400',
    'from-orange-500 to-orange-400'
  ];
  
  return (
    <div className="flex items-end justify-between h-40 gap-3">
      {data.map((item, index) => (
        <div key={index} className="flex flex-col items-center flex-1">
          <span className="text-sm font-semibold text-white mb-1">{item.count}</span>
          <motion.div
            className={`w-full bg-gradient-to-t ${colors[index]} rounded-t-md min-h-[4px]`}
            initial={{ height: 0 }}
            animate={{ height: `${(item.count / maxCount) * 100}%` }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          />
          <span className="text-sm text-slate-400 mt-2 font-medium">{item.name.slice(0, 3)}</span>
        </div>
      ))}
    </div>
  );
};

// Activity type breakdown
const ActivityBreakdown = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        Sin actividad registrada en este período
      </div>
    );
  }
  
  const total = data.reduce((sum, item) => sum + item.count, 0);
  const colors = [
    'bg-blue-500',
    'bg-cyan-500',
    'bg-teal-500',
    'bg-green-500',
    'bg-amber-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-red-500'
  ];
  
  return (
    <div className="space-y-3">
      {data.slice(0, 8).map((item, index) => (
        <div key={item.type} className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${colors[index % colors.length]}`}></div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-white">{item.label}</span>
              <span className="text-sm font-semibold text-slate-300">
                {item.count} ({Math.round((item.count / total) * 100)}%)
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${colors[index % colors.length]}`}
                initial={{ width: 0 }}
                animate={{ width: `${(item.count / total) * 100}%` }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminActivityMetrics = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);
  
  useEffect(() => {
    fetchGlobalActivity();
  }, [period]);
  
  const fetchGlobalActivity = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/analytics/activity/global?days=${period}`);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching global activity:', err);
      setError('Error al cargar métricas globales');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-slate-400">Cargando métricas globales...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <p className="text-red-400">{error}</p>
        <button 
          onClick={fetchGlobalActivity}
          className="mt-4 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="admin-activity-metrics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-['Outfit']">Métricas de Actividad Global</h2>
            <p className="text-sm text-slate-400">Patrones de uso de toda la plataforma</p>
          </div>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(Number(e.target.value))}
          className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-slate-300"
          data-testid="admin-activity-period-select"
        >
          <option value={7}>Últimos 7 días</option>
          <option value={30}>Últimos 30 días</option>
          <option value={90}>Últimos 90 días</option>
          <option value={180}>Últimos 6 meses</option>
        </select>
      </div>
      
      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-400 uppercase tracking-wide">Usuarios Activos</span>
            <Users className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-3xl font-bold text-white">{data?.active_users || 0}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-500/10 to-teal-500/10 border border-green-500/20 rounded-xl p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-400 uppercase tracking-wide">Total Actividades</span>
            <Activity className="w-5 h-5 text-green-400" />
          </div>
          <p className="text-3xl font-bold text-white">
            {data?.peak_analysis?.total_activities || 0}
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-400 uppercase tracking-wide">Hora Pico</span>
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <p className="text-3xl font-bold text-white">
            {data?.peak_analysis?.peak_hour !== null 
              ? `${data.peak_analysis.peak_hour}:00`
              : '--'}
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-400 uppercase tracking-wide">Esta Semana</span>
            <Calendar className="w-5 h-5 text-cyan-400" />
          </div>
          <p className="text-3xl font-bold text-white">
            {data?.weekly_comparison?.this_week || 0}
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-400 uppercase tracking-wide">Tendencia</span>
            {data?.weekly_comparison?.trend === 'up' ? (
              <TrendingUp className="w-5 h-5 text-green-400" />
            ) : data?.weekly_comparison?.trend === 'down' ? (
              <TrendingDown className="w-5 h-5 text-red-400" />
            ) : (
              <Activity className="w-5 h-5 text-slate-400" />
            )}
          </div>
          <p className={`text-3xl font-bold ${
            data?.weekly_comparison?.trend === 'up' 
              ? 'text-green-400' 
              : data?.weekly_comparison?.trend === 'down'
                ? 'text-red-400'
                : 'text-white'
          }`}>
            {data?.weekly_comparison?.change_percent > 0 ? '+' : ''}
            {data?.weekly_comparison?.change_percent || 0}%
          </p>
        </motion.div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly activity */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card border border-border rounded-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-blue-400" />
              <h3 className="font-semibold text-white">Actividad por Hora del Día</h3>
            </div>
            <span className="text-xs text-slate-400">Últimos {period} días</span>
          </div>
          <HourlyChart data={data?.hourly_activity} />
        </motion.div>
        
        {/* Daily activity */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-card border border-border rounded-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-green-400" />
              <h3 className="font-semibold text-white">Actividad por Día de la Semana</h3>
            </div>
          </div>
          <DailyChart data={data?.daily_activity} />
        </motion.div>
      </div>
      
      {/* Activity breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-card border border-border rounded-xl p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-purple-400" />
            <h3 className="font-semibold text-white">Desglose por Tipo de Actividad</h3>
          </div>
        </div>
        <ActivityBreakdown data={data?.activity_breakdown} />
      </motion.div>
      
      {/* Insights */}
      {data?.peak_analysis?.insights?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-6"
        >
          <div className="flex items-center space-x-2 mb-4">
            <Zap className="w-5 h-5 text-amber-400" />
            <h3 className="font-semibold text-white">Insights de la Plataforma</h3>
          </div>
          <div className="space-y-3">
            {data.peak_analysis.insights.map((insight, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                {insight.type === 'peak_hour' ? (
                  <Clock className="w-4 h-4 text-amber-400" />
                ) : (
                  <Activity className="w-4 h-4 text-blue-400" />
                )}
                <p className="text-sm text-slate-300">{insight.message}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminActivityMetrics;
